import { MYNAVI_PRIMARY } from '@/components/Campaign/mynavi/Color'
import { MyNaviButton } from '@/components/Campaign/mynavi/MyNaviButton/MyNaviButton'
import { MyNaviTypography } from '@/components/Campaign/mynavi/MyNaviUI/MyNaviTypography'
import { QuestionnaireFormField } from '@/components/Layout/questionnaire/QuestionnaireFormField'
import type { QuestionnaireAnswerFormData } from '@/schemas/forms/questionnaire'
import type { QuestionnaireData } from '@/schemas/v2/questionnaire'
import { Input, InputWrapper, MantineProvider, Modal, createTheme } from '@mantine/core'
import { memo } from 'react'
import type { CSSProperties } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

// Mantine の component テーマでラベルスタイルをスコープ管理する（CSS クラス名依存を避ける）
// iOS Safari の自動ズーム防止のため --input-fz も 16px に統一する
const myNaviFormTheme = createTheme({
  components: {
    Input: Input.extend({
      styles: {
        wrapper: { '--input-fz': '16px' } as CSSProperties,
      },
    }),
    InputWrapper: InputWrapper.extend({
      styles: {
        label: { fontSize: '1rem', fontWeight: 700, color: '#686868' },
        error: { marginTop: '8px' },
      },
    }),
  },
})

type Props = {
  questionnaire: QuestionnaireData | null
  isOpen: boolean
  /** サブミット中のローディング制御 */
  isSubmitting?: boolean
  /** APIエラー時のエラーメッセージ */
  submitError?: string | null
  onClose: () => void
  onSubmit: (answers: QuestionnaireAnswerFormData) => Promise<boolean | undefined>
}

export const MyNaviDialogQuestionnaire = memo(
  ({ questionnaire, isOpen, isSubmitting = false, submitError, onClose, onSubmit }: Props) => {
    const { control, handleSubmit, reset } = useForm<QuestionnaireAnswerFormData>()
    const { t } = useTranslation()

    if (!questionnaire) return null

    const items = questionnaire.items ?? []

    const handleClose = () => {
      // 送信中は Esc キー含むすべての閉じ操作をブロック
      if (isSubmitting) return
      reset()
      onClose()
    }

    const handleFormSubmit = async (data: QuestionnaireAnswerFormData) => {
      const success = await onSubmit(data)
      // false のときのみリセットを抑制（void/undefined は成功扱い）
      if (success !== false) {
        reset()
      }
    }

    return (
      <Modal
        opened={isOpen}
        onClose={handleClose}
        withCloseButton={false}
        closeOnClickOutside={false}
        closeOnEscape={!isSubmitting}
        centered
        size={520}
        styles={{
          // タイトルを完全センター揃えにする
          header: { justifyContent: 'center', paddingBottom: 8 },
          title: { flex: 1 },
        }}
        title={
          <MyNaviTypography
            variant="large-title"
            as="p"
            className="text-center w-full m-0"
            style={{ color: MYNAVI_PRIMARY }}
          >
            {questionnaire.title ?? t('mynavi.questionnaire.title', '会員登録時アンケート')}
          </MyNaviTypography>
        }
      >
        {/* MantineProvider でラベルスタイルをスコープ適用（CSS クラス名依存を避ける） */}
        <MantineProvider theme={myNaviFormTheme}>
          <form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col gap-0">
            <div className="space-y-6 overflow-y-auto max-h-[55vh] pb-2 px-1">
              {items.map((item) => (
                <QuestionnaireFormField key={item.id} item={item} control={control} />
              ))}
              {items.length === 0 && (
                <MyNaviTypography variant="body" as="p" className="text-center py-4">
                  {t('register_questionnaire.no_questions', '設問がありません')}
                </MyNaviTypography>
              )}
            </div>
            <div className="flex flex-col items-center pt-4 pb-2 border-t border-gray-100 mt-4 gap-4">
              {submitError && (
                <MyNaviTypography variant="body" as="p" className="text-center text-red-600 text-xs">
                  {submitError}
                </MyNaviTypography>
              )}
              <MyNaviButton
                label={t('register_questionnaire.submit', '回答する')}
                type="submit"
                loading={isSubmitting}
                disabled={isSubmitting}
                showIcon={false}
                fullWidth={false}
              />
            </div>
          </form>
        </MantineProvider>
      </Modal>
    )
  },
)

MyNaviDialogQuestionnaire.displayName = 'MyNaviDialogQuestionnaire'
